import { create } from "zustand";

// Simple cuid-style id
const uid = () => Math.random().toString(36).slice(2, 11);

const blankPage = (name = "Page 1") => ({
  id: uid(),
  name,
  background: "#FFFFFF",
  elements: [],
});

export const useEditorStore = create((set, get) => ({
  // Project metadata
  projectId: null,
  projectName: "Untitled Project",
  pages: [blankPage("Page 1")],
  activePageId: null,
  selectedElementId: null,
  zoom: 0.7,

  // History
  history: [],
  future: [],

  setProject: (project) => {
    const pages = project.pages?.length ? project.pages : [blankPage("Page 1")];
    set({
      projectId: project.id,
      projectName: project.name,
      pages,
      activePageId: pages[0]?.id || null,
      selectedElementId: null,
      history: [],
      future: [],
    });
  },

  setProjectName: (name) => set({ projectName: name }),
  setZoom: (z) => set({ zoom: Math.max(0.25, Math.min(2, z)) }),

  pushHistory: () => {
    const { pages, history } = get();
    set({ history: [...history.slice(-49), JSON.stringify(pages)], future: [] });
  },

  undo: () => {
    const { history, pages, future } = get();
    if (!history.length) return;
    const prev = history[history.length - 1];
    set({
      pages: JSON.parse(prev),
      history: history.slice(0, -1),
      future: [JSON.stringify(pages), ...future].slice(0, 50),
    });
  },

  redo: () => {
    const { future, pages, history } = get();
    if (!future.length) return;
    const next = future[0];
    set({
      pages: JSON.parse(next),
      future: future.slice(1),
      history: [...history, JSON.stringify(pages)].slice(-50),
    });
  },

  // ---------- Page actions ----------
  addPage: (afterId = null) => {
    get().pushHistory();
    const pages = [...get().pages];
    const newPage = blankPage(`Page ${pages.length + 1}`);
    if (afterId) {
      const idx = pages.findIndex((p) => p.id === afterId);
      pages.splice(idx + 1, 0, newPage);
    } else {
      pages.push(newPage);
    }
    set({ pages, activePageId: newPage.id });
  },

  duplicatePage: (pageId) => {
    get().pushHistory();
    const pages = [...get().pages];
    const idx = pages.findIndex((p) => p.id === pageId);
    if (idx < 0) return;
    const src = pages[idx];
    const copy = {
      ...JSON.parse(JSON.stringify(src)),
      id: uid(),
      name: `${src.name} (copy)`,
      elements: src.elements.map((el) => ({ ...el, id: uid() })),
    };
    pages.splice(idx + 1, 0, copy);
    set({ pages, activePageId: copy.id });
  },

  deletePage: (pageId) => {
    const { pages } = get();
    if (pages.length <= 1) return;
    get().pushHistory();
    const next = pages.filter((p) => p.id !== pageId);
    set({
      pages: next,
      activePageId: next[0].id,
      selectedElementId: null,
    });
  },

  movePage: (pageId, direction) => {
    const { pages } = get();
    const idx = pages.findIndex((p) => p.id === pageId);
    if (idx < 0) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= pages.length) return;
    get().pushHistory();
    const next = [...pages];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    set({ pages: next });
  },

  setPageBackground: (pageId, color) => {
    get().pushHistory();
    set({
      pages: get().pages.map((p) => (p.id === pageId ? { ...p, background: color } : p)),
    });
  },

  setActivePage: (pageId) => set({ activePageId: pageId, selectedElementId: null }),

  // ---------- Element actions ----------
  addElement: (pageId, element) => {
    get().pushHistory();
    const newEl = { id: uid(), ...element };
    set({
      pages: get().pages.map((p) =>
        p.id === pageId ? { ...p, elements: [...p.elements, newEl] } : p,
      ),
      selectedElementId: newEl.id,
      activePageId: pageId,
    });
    return newEl.id;
  },

  updateElement: (pageId, elementId, patch, { skipHistory = false } = {}) => {
    if (!skipHistory) get().pushHistory();
    set({
      pages: get().pages.map((p) =>
        p.id === pageId
          ? {
              ...p,
              elements: p.elements.map((el) => (el.id === elementId ? { ...el, ...patch } : el)),
            }
          : p,
      ),
    });
  },

  deleteElement: (pageId, elementId) => {
    get().pushHistory();
    set({
      pages: get().pages.map((p) =>
        p.id === pageId ? { ...p, elements: p.elements.filter((e) => e.id !== elementId) } : p,
      ),
      selectedElementId: null,
    });
  },

  setSelectedElement: (elementId, pageId = null) =>
    set({ selectedElementId: elementId, activePageId: pageId ?? get().activePageId }),

  // ---------- Layer ops ----------
  changeLayer: (pageId, elementId, op) => {
    get().pushHistory();
    const pages = get().pages.map((p) => {
      if (p.id !== pageId) return p;
      const elements = [...p.elements];
      const idx = elements.findIndex((e) => e.id === elementId);
      if (idx < 0) return p;
      const [target] = elements.splice(idx, 1);
      if (op === "front") elements.push(target);
      else if (op === "back") elements.unshift(target);
      else if (op === "forward") elements.splice(Math.min(elements.length, idx + 1), 0, target);
      else if (op === "backward") elements.splice(Math.max(0, idx - 1), 0, target);
      return { ...p, elements };
    });
    set({ pages });
  },

  // ---------- Page background image (drag-drop full cover) ----------
  setPageBackgroundImage: (pageId, imageUrl) => {
    get().pushHistory();
    set({
      pages: get().pages.map((p) =>
        p.id === pageId ? { ...p, backgroundImage: imageUrl, background: "#FFFFFF" } : p,
      ),
    });
  },

  clearPageBackgroundImage: (pageId) => {
    get().pushHistory();
    set({
      pages: get().pages.map((p) => {
        if (p.id !== pageId) return p;
        const { backgroundImage, ...rest } = p;
        return rest;
      }),
    });
  },
}));

export const newBlankPage = blankPage;
